import React, { useState, useEffect } from 'react';
import { adminAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Mail,
  Send,
  Users,
  Clock,
  CheckCircle2,
  XCircle,
  Plus,
  X,
  Loader2,
  History,
  Eye,
  Search,
  UserCheck
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdminEmailComposer() {
  const [activeTab, setActiveTab] = useState('compose');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Compose form state
  const [toEmails, setToEmails] = useState([]);
  const [emailInput, setEmailInput] = useState('');
  const [ccEmails, setCcEmails] = useState([]);
  const [ccInput, setCcInput] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isHtml, setIsHtml] = useState(false);
  
  // User selection
  const [availableUsers, setAvailableUsers] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [showUserPicker, setShowUserPicker] = useState(false);
  
  // Email logs
  const [emailLogs, setEmailLogs] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [logDetailOpen, setLogDetailOpen] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchEmailLogs();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await adminAPI.getUserEmailsForCompose();
      setAvailableUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const fetchEmailLogs = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getEmailLogs(50);
      setEmailLogs(response.data || []);
    } catch (error) {
      console.error('Failed to fetch email logs');
    } finally {
      setLoading(false);
    }
  };

  const addEmail = (email, type = 'to') => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) {
      toast.error('Invalid email format');
      return;
    }
    
    if (type === 'to') {
      if (toEmails.includes(trimmed)) {
        toast.error('Email already added');
        return;
      }
      setToEmails([...toEmails, trimmed]);
      setEmailInput('');
    } else {
      if (ccEmails.includes(trimmed)) {
        toast.error('Email already in CC');
        return;
      }
      setCcEmails([...ccEmails, trimmed]);
      setCcInput('');
    }
  };

  const removeEmail = (email, type = 'to') => {
    if (type === 'to') {
      setToEmails(toEmails.filter(e => e !== email));
    } else {
      setCcEmails(ccEmails.filter(e => e !== email));
    }
  };

  const handleKeyPress = (e, type) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const input = type === 'to' ? emailInput : ccInput;
      addEmail(input, type);
    }
  };

  const addUserEmail = (user) => {
    if (!toEmails.includes(user.email)) {
      setToEmails([...toEmails, user.email]);
    }
    setShowUserPicker(false);
    setUserSearchQuery('');
  };

  const filteredUsers = availableUsers.filter(user => 
    user.email.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
    user.name.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
    (user.organization && user.organization.toLowerCase().includes(userSearchQuery.toLowerCase()))
  );

  const handleSend = async () => {
    if (toEmails.length === 0) {
      toast.error('Please add at least one recipient');
      return;
    }
    if (!subject.trim()) {
      toast.error('Please enter a subject');
      return;
    }
    if (!body.trim()) {
      toast.error('Please enter email content');
      return;
    }

    setSending(true);
    try {
      const response = await adminAPI.composeEmail({
        to_emails: toEmails,
        cc_emails: ccEmails.length > 0 ? ccEmails : null,
        subject: subject.trim(),
        body: body.trim(),
        is_html: isHtml
      });
      
      toast.success(`Email sent to ${toEmails.length} recipient(s)`);
      
      // Reset form
      setToEmails([]);
      setCcEmails([]);
      setSubject('');
      setBody('');
      setIsHtml(false);
      
      // Refresh logs
      fetchEmailLogs();
      setActiveTab('history');
      
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send email';
      toast.error(message);
    } finally {
      setSending(false);
    }
  };

  const viewLogDetail = async (log) => {
    try {
      const response = await adminAPI.getEmailLogDetail(log.id);
      setSelectedLog(response.data);
      setLogDetailOpen(true);
    } catch (error) {
      toast.error('Failed to load email details');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-email-composer">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Email Composer</h1>
          <p className="text-slate-600 mt-1">
            Send emails to users from admin@archerymanagerai.com
          </p>
        </div>
        <Badge variant="outline" className="text-sm w-fit">
          <Mail className="w-4 h-4 mr-1" />
          Powered by Resend
        </Badge>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="compose" className="flex items-center gap-2">
            <Send className="w-4 h-4" />
            Compose
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="w-4 h-4" />
            Sent History
          </TabsTrigger>
        </TabsList>

        {/* Compose Tab */}
        <TabsContent value="compose" className="mt-6">
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary" />
                New Email
              </CardTitle>
              <CardDescription>
                Compose and send an email to users or external contacts
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* From field (read-only) */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">From</Label>
                <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border">
                  <Mail className="w-4 h-4 text-slate-500" />
                  <span className="text-slate-700">admin@archerymanagerai.com</span>
                  <Badge variant="secondary" className="ml-auto text-xs">Admin</Badge>
                </div>
              </div>

              {/* To field */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">To *</Label>
                  <Dialog open={showUserPicker} onOpenChange={setShowUserPicker}>
                    <DialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-primary">
                        <UserCheck className="w-4 h-4 mr-1" />
                        Select Users
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle>Select Recipients</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input
                            placeholder="Search by name, email, or organization..."
                            value={userSearchQuery}
                            onChange={(e) => setUserSearchQuery(e.target.value)}
                            className="pl-10"
                          />
                        </div>
                        <ScrollArea className="h-[300px]">
                          <div className="space-y-2">
                            {filteredUsers.map((user, idx) => (
                              <button
                                key={idx}
                                onClick={() => addUserEmail(user)}
                                className="w-full text-left p-3 rounded-lg hover:bg-slate-100 transition-colors border"
                                disabled={toEmails.includes(user.email)}
                              >
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="font-medium text-slate-900">{user.name}</p>
                                    <p className="text-sm text-slate-500">{user.email}</p>
                                  </div>
                                  <div className="text-right">
                                    <Badge variant="outline" className="text-xs">{user.role}</Badge>
                                    {user.organization && (
                                      <p className="text-xs text-slate-400 mt-1">{user.organization}</p>
                                    )}
                                  </div>
                                </div>
                                {toEmails.includes(user.email) && (
                                  <Badge variant="secondary" className="mt-2 text-xs">Added</Badge>
                                )}
                              </button>
                            ))}
                            {filteredUsers.length === 0 && (
                              <p className="text-center text-slate-500 py-8">No users found</p>
                            )}
                          </div>
                        </ScrollArea>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                
                <div className="flex flex-wrap gap-2 p-3 min-h-[48px] bg-white border rounded-lg">
                  {toEmails.map((email) => (
                    <Badge key={email} variant="secondary" className="flex items-center gap-1 py-1">
                      {email}
                      <button onClick={() => removeEmail(email, 'to')} className="ml-1 hover:text-red-600">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                  <Input
                    type="email"
                    placeholder="Enter email and press Enter..."
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    onKeyDown={(e) => handleKeyPress(e, 'to')}
                    onBlur={() => emailInput && addEmail(emailInput, 'to')}
                    className="flex-1 min-w-[200px] border-0 shadow-none focus-visible:ring-0 p-0"
                    data-testid="to-email-input"
                  />
                </div>
              </div>

              {/* CC field (optional) */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">CC (optional)</Label>
                <div className="flex flex-wrap gap-2 p-3 min-h-[48px] bg-white border rounded-lg">
                  {ccEmails.map((email) => (
                    <Badge key={email} variant="outline" className="flex items-center gap-1 py-1">
                      {email}
                      <button onClick={() => removeEmail(email, 'cc')} className="ml-1 hover:text-red-600">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                  <Input
                    type="email"
                    placeholder="Add CC recipients..."
                    value={ccInput}
                    onChange={(e) => setCcInput(e.target.value)}
                    onKeyDown={(e) => handleKeyPress(e, 'cc')}
                    onBlur={() => ccInput && addEmail(ccInput, 'cc')}
                    className="flex-1 min-w-[200px] border-0 shadow-none focus-visible:ring-0 p-0"
                  />
                </div>
              </div>

              {/* Subject */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Subject *</Label>
                <Input
                  placeholder="Enter email subject..."
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  data-testid="email-subject-input"
                />
              </div>

              {/* Body */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Message *</Label>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="html-mode" className="text-xs text-slate-500">HTML Mode</Label>
                    <Switch
                      id="html-mode"
                      checked={isHtml}
                      onCheckedChange={setIsHtml}
                    />
                  </div>
                </div>
                <Textarea
                  placeholder={isHtml ? "Enter HTML content..." : "Type your message here..."}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={10}
                  className={isHtml ? "font-mono text-sm" : ""}
                  data-testid="email-body-input"
                />
                {!isHtml && (
                  <p className="text-xs text-slate-500">
                    Your message will be wrapped in a professional email template with Archery Manager AI branding.
                  </p>
                )}
              </div>

              {/* Send button */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    setToEmails([]);
                    setCcEmails([]);
                    setSubject('');
                    setBody('');
                  }}
                >
                  Clear
                </Button>
                <Button
                  className="btn-primary"
                  onClick={handleSend}
                  disabled={sending || toEmails.length === 0}
                  data-testid="send-email-btn"
                >
                  {sending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Email
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="mt-6">
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5 text-primary" />
                Sent Emails
              </CardTitle>
              <CardDescription>
                History of emails sent from the admin panel
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : emailLogs.length > 0 ? (
                <div className="space-y-3">
                  {emailLogs.map((log) => (
                    <div
                      key={log.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {log.status === 'sent' ? (
                            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-500" />
                          )}
                          <span className="font-medium text-slate-900">{log.subject}</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {log.to_emails.length} recipient(s)
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(log.sent_at)}
                          </span>
                          <span>by {log.sent_by}</span>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => viewLogDetail(log)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Mail className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">No emails sent yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Email Detail Dialog */}
      <Dialog open={logDetailOpen} onOpenChange={setLogDetailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Email Details</DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs text-slate-500">Status</Label>
                <Badge variant={selectedLog.status === 'sent' ? 'default' : 'destructive'}>
                  {selectedLog.status === 'sent' ? (
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                  ) : (
                    <XCircle className="w-3 h-3 mr-1" />
                  )}
                  {selectedLog.status}
                </Badge>
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs text-slate-500">Subject</Label>
                <p className="font-medium">{selectedLog.subject}</p>
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs text-slate-500">Recipients ({selectedLog.to_emails?.length})</Label>
                <div className="flex flex-wrap gap-1">
                  {selectedLog.to_emails?.map((email, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs">{email}</Badge>
                  ))}
                </div>
              </div>
              
              {selectedLog.cc_emails?.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">CC</Label>
                  <div className="flex flex-wrap gap-1">
                    {selectedLog.cc_emails.map((email, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">{email}</Badge>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Label className="text-xs text-slate-500">Message Preview</Label>
                <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-700 max-h-[200px] overflow-auto">
                  {selectedLog.body_preview}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs text-slate-500">Sent</Label>
                <p className="text-sm">
                  {formatDate(selectedLog.sent_at)} by {selectedLog.sent_by_name || selectedLog.sent_by_email}
                </p>
              </div>
              
              {selectedLog.error && (
                <Alert variant="destructive">
                  <AlertDescription>{selectedLog.error}</AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
